import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, Drawer,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography
} from "@mui/material";
import { Check, ChevronDown, ChevronUp, Copy, History, RefreshCw, X } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const dateParts = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return { day: "—", time: "—" };
  return {
    day: `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`,
    time: `${match[4]}:${match[5]}`,
  };
};
const showValue = (value) => value === null || value === undefined || value === "" ? "—" : value;
const showQuantity = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("en-US").format(number) : value;
};
const details = [
  ["Operation", "OpDesctiption"], ["Setup time", "SetupTime"],
  ["Operation time", "OperationalTime"], ["Setup + operation", "SetupAndOpeTime"],
  ["Time unit", "TimeUnit"], ["Operation start", "opstartDate"],
  ["Operation end", "opendDate"], ["WO due date", "WOduedate"]
];

export default function PlanningHistoryDrawer({ jobNumber, onClose, onSelectJob }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [machineFilter, setMachineFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [nearby, setNearby] = useState([]);
  const [copyState, setCopyState] = useState("");

  useEffect(() => {
    setMachineFilter("all");
    setFromDate("");
    setToDate("");
    setNearby([]);
    setCopyState("");
  }, [jobNumber]);

  useEffect(() => {
    if (!jobNumber) return undefined;
    const controller = new AbortController();
    setResult(null);
    setError("");
    setExpandedId(null);
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/planning-history`, {
      params: { jobNumber }, signal: controller.signal
    }).then(({ data }) => setResult(data))
      .catch(err => {
        if (err.code !== "ERR_CANCELED") setError(err.response?.data?.error || (err.response?.status === 404
          ? "History API not found. Start the backend history branch on port 8800."
          : "Could not load planning history. Check the backend connection and logs."));
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [jobNumber, refresh]);

  const records = (result?.records || []).slice().sort((a, b) =>
    String(b.planningFrom || "").localeCompare(String(a.planningFrom || "")) ||
    String(a.machineCode || "").localeCompare(String(b.machineCode || "")) ||
    Number(a.jobSequenceNumber || 0) - Number(b.jobSequenceNumber || 0)
  );
  const machineCount = new Set(records.map(record => record.machineCode).filter(Boolean)).size;
  const machineOptions = [...new Set(records.map(record => record.machineCode).filter(Boolean))].sort();
  const visibleRecords = records.filter(record => {
    const date = String(record.planningFrom || "").slice(0, 10);
    return (machineFilter === "all" || record.machineCode === machineFilter) &&
      (!fromDate || date >= fromDate) && (!toDate || date <= toDate);
  });

  useEffect(() => {
    if (!jobNumber || !result || result.records?.length || error) return undefined;
    const controller = new AbortController();
    axios.get(`${API_BASE_URL}/api/planning-history/suggestions`, {
      params: { q: jobNumber }, signal: controller.signal
    }).then(({ data }) => setNearby((data.suggestions || []).filter(item =>
      item.jobNumber.toUpperCase() !== jobNumber.toUpperCase()
    ))).catch(() => { if (!controller.signal.aborted) setNearby([]); });
    return () => controller.abort();
  }, [jobNumber, result, error]);

  const copyJobNumber = async () => {
    try {
      await navigator.clipboard.writeText(jobNumber);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy unavailable");
    }
  };


  return (
    <Drawer anchor="right" open={Boolean(jobNumber)} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 720 }, maxWidth: "100%" } }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: "auto" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box>
            <Typography variant="overline" color="primary.main" fontWeight={700}>Job planning timeline</Typography>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h5" component="h2" fontWeight={800}>{jobNumber}</Typography>
              <Button size="small" onClick={copyJobNumber} startIcon={copyState === "Copied" ? <Check size={16} /> : <Copy size={16} />}
                aria-label="Copy job number">{copyState || "Copy"}</Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Current plan entries, newest planning window first
            </Typography>
          </Box>
          <Button onClick={onClose} aria-label="Close planning timeline" startIcon={<X size={18} />}>
            Close
          </Button>
        </Stack>

        {!loading && !error && result && <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ my: 2.5 }}>
          <Chip icon={<History size={16} />} label={`${records.length} plan ${records.length === 1 ? "entry" : "entries"}`}
            color="primary" variant="outlined" />
          <Chip label={`${machineCount} ${machineCount === 1 ? "machine" : "machines"}`} variant="outlined" />
          {records.length > 0 && <Chip label={`Latest: ${dateParts(records[0].planningFrom).day}`} variant="outlined" />}
          <Button variant="text" size="small" startIcon={<RefreshCw size={16} />}
            onClick={() => setRefresh(value => value + 1)} disabled={loading} sx={{ ml: { sm: "auto" } }}>
            Refresh
          </Button>
        </Stack>}

        {loading && <Box role="status" sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" action={<Button onClick={() => setRefresh(value => value + 1)}>Retry</Button>}>{error}</Alert>}
        {!loading && !error && result?.truncated && <Alert severity="info" sx={{ mb: 2 }}>
          Showing the newest 250 plan entries for this job.
        </Alert>}
        {!loading && !error && result && records.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No current plan matches this exact job number.
            {nearby.length > 0 && <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Did you mean one of these?</Typography>
              <Stack gap={1}>
                {nearby.map(item => (
                  <Button key={item.jobNumber} variant="outlined" onClick={() => onSelectJob(item.jobNumber)}
                    sx={{ justifyContent: "flex-start", textAlign: "left", textTransform: "none", py: 1 }}>
                    <Box>
                      <Typography fontWeight={700}>{item.jobNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.customer || "Customer unavailable"} · {item.machines || "—"} · {item.entryCount} plans
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            </Box>}
          </Alert>
        )}

        {!loading && !error && records.length > 1 && (
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { sm: 140 } }}>
              <InputLabel>Machine</InputLabel>
              <Select label="Machine" value={machineFilter} onChange={event => setMachineFilter(event.target.value)}>
                <MenuItem value="all">All machines</MenuItem>
                {machineOptions.map(machine => <MenuItem key={machine} value={machine}>{machine}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField type="date" size="small" label="From plan date" value={fromDate}
              onChange={event => setFromDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" size="small" label="To plan date" value={toDate}
              onChange={event => setToDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            <Button size="small" onClick={() => { setMachineFilter("all"); setFromDate(""); setToDate(""); }}
              disabled={machineFilter === "all" && !fromDate && !toDate}>Clear</Button>
            <Typography variant="caption" color="text.secondary">
              {visibleRecords.length} of {records.length} entries
            </Typography>
          </Stack>
        )}
        {!loading && !error && records.length > 0 && visibleRecords.length === 0 && (
          <Alert severity="info">No plans match these filters.</Alert>
        )}

        {!loading && !error && visibleRecords.length > 0 && (
          <Box sx={{ borderLeft: 2, borderColor: "divider", ml: 1.25, pl: 3 }}>
            {visibleRecords.map((record) => {
              const isLatest = record.id === records[0].id;
              const earlier = records.find(other => other.machineCode === record.machineCode &&
                other.OpDesctiption === record.OpDesctiption && other.TimeUnit === record.TimeUnit &&
                record.OPQty != null && other.OPQty != null && record.WOQty != null && other.WOQty != null &&
                Number(other.OPQty) === Number(record.OPQty) && Number(other.WOQty) === Number(record.WOQty) &&
                String(other.planningFrom || "") < String(record.planningFrom || ""));
              const comparable = earlier && record.plannedQty != null && earlier.plannedQty != null &&
                Number.isFinite(Number(record.plannedQty)) && Number.isFinite(Number(earlier.plannedQty));
              const delta = comparable ? Number(record.plannedQty) - Number(earlier.plannedQty) : null;
              const start = dateParts(record.planningFrom);
              const end = dateParts(record.planingTo);
              const isExpanded = expandedId === record.id;
              return (
                <Box key={record.id} sx={{ position: "relative", pb: 2.5,
                  "&::before": {
                    content: '""', position: "absolute", top: 22, left: -32,
                    width: 14, height: 14, borderRadius: "50%",
                    bgcolor: isLatest ? "primary.main" : "background.paper",
                    border: 2, borderColor: isLatest ? "primary.main" : "text.disabled",
                    boxShadow: isLatest ? 2 : 0,
                  }
                }}>
                  <Box sx={{
                    border: 1, borderColor: isLatest ? "primary.main" : "divider",
                    borderRadius: 2, overflow: "hidden", bgcolor: "background.paper",
                    boxShadow: isLatest ? 2 : 0
                  }}>
                    <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={800}>{start.day}</Typography>
                        <Chip size="small" label={showValue(record.machineCode)} color={isLatest ? "primary" : "default"} />
                        {isLatest && <Chip size="small" label="Latest plan" color="success" variant="outlined" />}
                        {record.jobSequenceNumber != null && <Typography variant="caption" color="text.secondary">
                          Sequence {record.jobSequenceNumber}
                        </Typography>}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Plan window: {start.time} → {end.day}, {end.time}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
                        {showValue(record.WOdescription)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {showValue(record.customer)}
                      </Typography>

                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                        gap: 1, p: 1.5, borderRadius: 1.5, bgcolor: "action.hover"
                      }}>
                        {[["Planned qty", record.plannedQty], ["OP qty", record.OPQty], ["WO qty", record.WOQty]]
                          .map(([label, value]) => (
                            <Box key={label}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body1" fontWeight={700}>{showQuantity(value)}</Typography>
                            </Box>
                          ))}
                      </Box>
                      {comparable && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Planned qty vs earlier entry on this machine and operation: {delta === 0
                            ? "no difference" : `${delta > 0 ? "+" : "−"}${showQuantity(Math.abs(delta))}`}
                          {" "}({dateParts(earlier.planningFrom).day}). Entries are separate plans, not a running total.
                        </Typography>
                      )}
                      <Button size="small" onClick={() => setExpandedId(isExpanded ? null : record.id)}
                        aria-expanded={isExpanded} aria-controls={`planning-details-${record.id}`}
                        endIcon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        sx={{ mt: 1.5, px: 0 }}>
                        {isExpanded ? "Hide details" : "View details"}
                      </Button>
                      <Collapse in={isExpanded}>
                        <Box id={`planning-details-${record.id}`}>
                          <Divider sx={{ my: 1.5 }} />
                          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
                            {details.map(([label, key]) => (
                              <Box key={key} sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{showValue(record[key])}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
